import { BsFillInfoCircleFill, BsFillPlayFill, BsFillStarFill } from "react-icons/bs";
import { motion } from "framer-motion";
import styled from "styled-components";
import Button from "../Button";
import { formatVoteAverage, getBackdropUrl } from "../../utils/movie";

const Section = styled.section`
  position: relative;
  min-height: clamp(520px, 72vh, 780px);
  display: flex;
  align-items: end;
  width: 100%;
  overflow: hidden;
  padding: 7rem 0 3rem;
`;

const Background = styled.div`
  position: absolute;
  inset: 0;
  background-image:
    linear-gradient(180deg, rgba(10, 10, 10, 0.15) 0%, rgba(10, 10, 10, 0.3) 38%, rgba(10, 10, 10, 0.94) 100%),
    linear-gradient(90deg, rgba(10, 10, 10, 0.9) 0%, rgba(10, 10, 10, 0.52) 38%, rgba(10, 10, 10, 0.06) 100%),
    url(${({ $background }) => $background});
  background-size: cover;
  background-position: center top;
  transform: scale(1.04);
`;

const Noise = styled.div`
  position: absolute;
  inset: 0;
  background-image: radial-gradient(rgba(255, 255, 255, 0.08) 0.7px, transparent 0.7px);
  background-size: 12px 12px;
  opacity: 0.08;
`;

const Content = styled(motion.div)`
  position: relative;
  z-index: 1;
  display: grid;
  gap: 1rem;
  width: min(720px, calc(100% - 1.5rem));
  margin: 0 auto;
  margin-left: max(1rem, calc((100vw - 1280px) / 2 + 1rem));

  @media (max-width: ${({ theme }) => theme.breakpoints.desktop}) {
    margin-left: auto;
  }
`;

const Eyebrow = styled.span`
  display: inline-flex;
  width: fit-content;
  align-items: center;
  gap: 0.6rem;
  padding: 0.45rem 0.8rem;
  border-radius: ${({ theme }) => theme.radii.pill};
  background: rgba(255, 255, 255, 0.08);
  color: ${({ theme }) => theme.colors.textMuted};
  text-transform: uppercase;
  letter-spacing: 0.14em;
  font-size: 0.75rem;
`;

const Title = styled.h1`
  margin: 0;
  font-family: ${({ theme }) => theme.fonts.heading};
  font-size: clamp(3.4rem, 8vw, 6.4rem);
  letter-spacing: 0.03em;
  line-height: 0.92;
`;

const Meta = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.85rem;
  color: ${({ theme }) => theme.colors.textMuted};
`;

const MetaItem = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
`;

const Overview = styled.p`
  margin: 0;
  max-width: 58ch;
  font-size: clamp(1rem, 2vw, 1.08rem);
  line-height: 1.75;
  color: ${({ theme }) => theme.colors.textMuted};
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 4;
  overflow: hidden;
`;

const GenreRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.6rem;
`;

const GenrePill = styled.span`
  padding: 0.4rem 0.8rem;
  border-radius: ${({ theme }) => theme.radii.pill};
  background: rgba(255, 255, 255, 0.08);
  color: ${({ theme }) => theme.colors.white};
  font-size: 0.84rem;
`;

const Actions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.85rem;
  padding-top: 0.3rem;
`;

function HeroSection({ movie, onPrimaryAction, onSecondaryAction }) {
  if (!movie) {
    return null;
  }

  const background = getBackdropUrl(movie.backdropPath);

  return (
    <Section>
      <Background $background={background} aria-hidden />
      <Noise aria-hidden />
      <Content
        initial={{ opacity: 0, y: 28 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease: "easeOut" }}
      >
        <Eyebrow>Em destaque agora</Eyebrow>
        <Title>{movie.title}</Title>
        <Meta>
          <MetaItem>
            <BsFillStarFill color="#f5c518" aria-hidden />
            {formatVoteAverage(movie.rating)}
          </MetaItem>
          <MetaItem>{movie.releaseYear}</MetaItem>
          {movie.genreNames?.slice(0, 3).map((genre) => (
            <MetaItem key={genre}>{genre}</MetaItem>
          ))}
        </Meta>
        <Overview>{movie.overview}</Overview>
        <GenreRow>
          {(movie.genreNames?.length ? movie.genreNames : ["Drama cinematografico", "Curadoria premium"]).slice(0, 4).map((genre) => (
            <GenrePill key={genre}>{genre}</GenrePill>
          ))}
        </GenreRow>
        <Actions>
          <Button icon={<BsFillPlayFill />} onClick={() => onPrimaryAction(movie.id)}>
            Assistir
          </Button>
          <Button variant="secondary" icon={<BsFillInfoCircleFill />} onClick={() => onSecondaryAction(movie.id)}>
            Mais informacoes
          </Button>
        </Actions>
      </Content>
    </Section>
  );
}

export default HeroSection;
